import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { NgTemplateOutlet } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	DestroyRef,
	effect,
	inject,
	Injector,
	input,
	OnInit,
	output,
	signal,
	TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, FormsModule, FormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { debounceTime, filter, finalize, map, Observable, switchMap } from 'rxjs';
import { last, take, tap } from 'rxjs/operators';

import { InfoboxService } from '../infobox/infobox.service';
import { TemplateTransformerService } from './template-transformer/template-transformer.service';
import { templateVariableValidator } from './forms-validators/template-variable-validator';
import { htmlTagsValidator } from './forms-validators/html-tags-validator';
import { BulkSendProgress, MessageGenerateAdapter, MessageTemplate } from './message-generate.model';
import { MailListValidatorDirective } from '@/_utils/validators/mail-list-validator.directive';

export enum MessageType {
	SMS = 'SMS',
	EMAIL = 'EMAIL',
}

@Component({
	selector: 'app-message-generate',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		MatInputModule,
		MatFormFieldModule,
		MatButtonModule,
		CdkCopyToClipboard,
		MatProgressSpinnerModule,
		MatSelectModule,
		FormsModule,
		RouterModule,
		MatProgressBarModule,
		NgTemplateOutlet,
		MailListValidatorDirective,
		MatTooltipModule,
	],
	templateUrl: './message-generate.component.html',
	styleUrl: './message-generate.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageGenerateComponent<TPreview, TSendResult = void> implements OnInit {
	readonly itemsIds = input.required<number[]>();
	readonly type = input.required<MessageType>();

	readonly adapter = input.required<MessageGenerateAdapter<TPreview, TSendResult>>();

	readonly previewTemplate = input<TemplateRef<{ $implicit: TPreview[]; previewLoader: boolean }>>();
	readonly attachmentsByItem = input<Record<number, File[]>>({});
	readonly closeDialog = output<void>();

	readonly templateControl = new FormControl('');
	readonly ccControl = new FormControl('');

	readonly templates = signal<MessageTemplate[]>([]);
	readonly loader = signal(false);
	readonly sendProgress = signal<number | null>(null);
	readonly previewLoader = signal(false);
	readonly selectedTemplate = signal<MessageTemplate>(null);
	readonly previewList = signal<TPreview[]>([]);

	readonly propertiesKeys = computed(() =>
		this.templateTransformerService.properties().map((v) => `{{ ${v.displayValue} }}`),
	);

	readonly isEmail = computed(() => this.type() === MessageType.EMAIL);

	readonly MessageType = MessageType;

	private readonly snackBar = inject(MatSnackBar);
	private readonly infoboxService = inject(InfoboxService);
	private readonly injector = inject(Injector);
	private readonly destroyRef = inject(DestroyRef);
	private readonly templateTransformerService = inject(TemplateTransformerService);
	private readonly cdr = inject(ChangeDetectorRef);

	ngOnInit(): void {
		this.initializeTemplateChange();
		this.initializeValidatorsUpdate();
		this.fetchTemplates();
		this.initTemplatePreviewListener();
	}

	initializeTemplateChange(): void {
		effect(
			() => {
				const rawContent = this.selectedTemplate()?.content ?? '';
				const replacedContent = this.templateTransformerService.replaceValuesWithKeys(rawContent);

				if (this.templateControl.value !== replacedContent) {
					this.templateControl.setValue(replacedContent);
					this.fetchTemplatePlaceholders();
				}
			},
			{ injector: this.injector, allowSignalWrites: true },
		);
	}

	initializeValidatorsUpdate(): void {
		effect(
			() => {
				this.templateControl.setValidators([
					Validators.required,
					templateVariableValidator(this.propertiesKeys()),
					htmlTagsValidator(),
				]);
				this.templateControl.updateValueAndValidity();
			},
			{ injector: this.injector },
		);
	}

	copied(code: string): void {
		this.snackBar.open(`Skopiowano ${code} do schowka`, '', { duration: 3000 });
	}

	initTemplatePreviewListener(): void {
		this.templateControl.valueChanges
			.pipe(
				filter(() => this.templateControl.valid),
				debounceTime(500),
				map((message) => this.templateTransformerService.replaceKeysWithValues(message)),
				switchMap((message) => this.loadPreview(message)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((preview) => this.previewList.set(preview));
	}

	send(): void {
		if (this.isEmail() && this.ccControl.invalid) {
			this.ccControl.markAsTouched();
			return;
		}

		this.loader.set(true);
		this.sendProgress.set(null);

		const cc = this.isEmail() ? this.parseMails(this.ccControl.value) : undefined;

		const attachments = this.attachmentsByItem();

		this.adapter()
			.sendBulk(this.previewList(), cc, attachments)
			.pipe(
				tap((value) => {
					const progress = (value as BulkSendProgress)?.progress;
					if (typeof progress === 'number') {
						this.sendProgress.set(progress);
						this.cdr.detectChanges();
					}
				}),
				filter((value) => {
					const progress = (value as BulkSendProgress)?.progress;
					return typeof progress !== 'number' || progress === 100;
				}),
				take(1),
				tap(() => {
					this.infoboxService.success('Wiadomości wysłane pomyślnie');
					this.closeDialog.emit();
				}),
				finalize(() => {
					this.loader.set(false);
					this.sendProgress.set(null);
				}),
			)
			.subscribe();
	}

	private parseMails(value: string | null | undefined): string[] {
		if (!value) {
			return [];
		}
		return value
			.split(',')
			.map((v) => v.trim())
			.filter((v) => v.length > 0);
	}

	private loadPreview(message: string): Observable<TPreview[]> {
		this.previewLoader.set(true);
		const previewPayload =
			this.type() === MessageType.EMAIL
				? { itemsIds: this.itemsIds(), message, subject: this.selectedTemplate()?.subject }
				: { itemsIds: this.itemsIds(), message };

		return this.adapter()
			.createPreview(previewPayload)
			.pipe(finalize(() => this.previewLoader.set(false)));
	}

	private fetchTemplates(): void {
		this.loader.set(true);
		this.adapter()
			.fetchTemplates()
			.pipe(finalize(() => this.loader.set(false)))
			.subscribe((templates) => this.templates.set(templates));
	}

	private fetchTemplatePlaceholders(): void {
		const templateId = this.selectedTemplate()?.id;
		if (!templateId) {
			return;
		}

		this.loader.set(true);
		this.adapter()
			.fetchPlaceholders(templateId)
			.pipe(finalize(() => this.loader.set(false)))
			.subscribe((placeholders) => this.templateTransformerService.properties.set(placeholders));
	}
}
